import { useCallback, useRef } from "react";
import Box from "@mui/material/Box";
import Slider from "@mui/material/Slider";
import Typography from "@mui/material/Typography";
import { useController } from "react-hook-form";
import { useTranslationApp } from "../../../lib/i18next";
import {
  TEMPLATES_LETTER_SPACING,
  TEMPLATES_LINE_SPACING,
} from "../../../i18n/keysTranslations";
import { BOX_SHADOW_FIELDS, INPUT_COLOR_BORDER } from "../../../utils/colors";
import { getReactHookFormNestedError } from "../../../utils/errors";
import { useSliderDragCommit } from "./useSliderDragCommit";

const optionalRules = { required: false };

const LINE_HEIGHT_STEP = 0.1;
const LINE_HEIGHT_SLIDER_MIN = 0;
const LINE_HEIGHT_SLIDER_MAX = 3;
const LINE_HEIGHT_FALLBACK = 1.2;
const LETTER_SPACING_STEP = 1;
const LETTER_SPACING_SLIDER_MIN = -20;
const LETTER_SPACING_SLIDER_MAX = 60;
const LETTER_SPACING_FALLBACK = 0;
const LETTER_SPACING_MARKS = [{ value: 0 }];

const NUMERIC_DRAFT = /^-?\d*[.,]?\d*$/;

const assignRef = (ref, element) => {
  if (!ref) return;
  if (typeof ref === "function") {
    ref(element);
    return;
  }
  ref.current = element;
};

const stepDecimals = (step) => {
  const text = String(step);
  const index = text.indexOf(".");
  return index === -1 ? 0 : text.length - index - 1;
};

const snapToStep = (value, step) => {
  const decimals = stepDecimals(step);
  return Number((Math.round(value / step) * step).toFixed(decimals));
};

const normalizeDecimalText = (value) => String(value ?? "").replace(",", ".");

const isIncompleteNumericDraft = (value) => {
  const text = String(value ?? "");
  return text === "-" || /[.,]$/.test(text);
};

const parseFieldNumber = (value) => {
  if (value === "" || value == null) return null;
  const normalized = normalizeDecimalText(value);
  if (normalized === "-" || normalized === "." || normalized === "-.") {
    return null;
  }
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const clampValue = (value, min, max) => Math.min(max, Math.max(min, value));

const SLIDER_THUMB_SIZE = 20;

const sliderSx = {
  flex: 1,
  minWidth: 0,
  ml: 0,
  color: "#000",
  ".MuiSlider-track": {
    backgroundColor: "#000",
    height: 4,
  },
  ".MuiSlider-rail": {
    backgroundColor: "rgba(0,0,0,0.5)",
    height: 4,
  },
  ".MuiSlider-mark": {
    backgroundColor: "transparent",
  },
  ".MuiSlider-thumb": {
    width: SLIDER_THUMB_SIZE,
    height: SLIDER_THUMB_SIZE,
    border: "1px solid #e1e1e1",
    backgroundColor: "#fff",
  },
};

const numberInputSx = {
  width: 64,
  flexShrink: 0,
  height: 36,
  px: 1,
  border: `1px solid ${INPUT_COLOR_BORDER}`,
  borderRadius: "10px",
  bgcolor: "fields.background",
  boxShadow: BOX_SHADOW_FIELDS,
  outline: "none",
  fontFamily: "inherit",
  fontSize: 14,
  fontWeight: 500,
  color: "#000",
  textAlign: "center",
  appearance: "none",
  "&:disabled": {
    color: "text.disabled",
  },
};

const SpacingSliderField = ({
  control,
  errors,
  name,
  label,
  disabled = false,
  step,
  sliderMin,
  sliderMax,
  fallback,
  marks,
  commitMin,
  onChangeValue,
}) => {
  const { field } = useController({
    control,
    name,
    rules: optionalRules,
  });
  const error = getReactHookFormNestedError(errors, name);
  const inputRef = useRef(null);
  const fieldRef = useRef(field.ref);
  const onChangeRef = useRef(onChangeValue);
  fieldRef.current = field.ref;
  onChangeRef.current = onChangeValue;

  const normalizeCommitValue = useCallback(
    (next) => {
      if (next === "" || next == null) return next;
      if (commitMin == null || typeof next !== "number") return next;
      return Math.max(commitMin, next);
    },
    [commitMin]
  );

  const commit = useCallback(
    (next) => {
      const normalized = normalizeCommitValue(next);
      field.onChange(normalized);
      onChangeRef.current(normalized === "" ? "" : normalized);
    },
    [field, normalizeCommitValue]
  );

  const drag = useSliderDragCommit(commit);

  const stepBy = useCallback(
    (direction) => {
      const current = parseFieldNumber(field.value);
      const base = current == null ? fallback : current;
      commit(snapToStep(base + direction * step, step));
    },
    [commit, fallback, field.value, step]
  );

  const setInputRef = useCallback((element) => {
    inputRef.current = element;
    assignRef(fieldRef.current, element);
  }, []);

  const numeric = parseFieldNumber(field.value);
  const sliderValue = clampValue(
    numeric == null ? fallback : numeric,
    sliderMin,
    sliderMax
  );
  const displayValue =
    field.value === "" || field.value == null ? String(fallback) : field.value;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Typography
        component="label"
        htmlFor={name}
        sx={{
          fontSize: 12,
          fontWeight: 500,
          color: "text.secondary",
        }}
      >
        {label}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Slider
          min={sliderMin}
          max={sliderMax}
          step={step}
          marks={marks}
          value={drag.dragValue ?? sliderValue}
          disabled={disabled}
          aria-label={label}
          onChange={(_event, value) => {
            const next = Array.isArray(value) ? value[0] : value;
            drag.onChange(typeof next === "number" ? next : sliderValue);
          }}
          onChangeCommitted={drag.onChangeCommitted}
          sx={sliderSx}
        />
        <Box
          component="input"
          id={name}
          name={name}
          type="text"
          inputMode="decimal"
          disabled={disabled}
          value={displayValue}
          aria-invalid={Boolean(error)}
          autoComplete="off"
          spellCheck={false}
          onChange={(event) => {
            const raw = event.target.value;
            if (raw !== "" && !NUMERIC_DRAFT.test(raw)) return;
            field.onChange(raw);
            if (raw === "") {
              onChangeRef.current("");
              return;
            }
            if (isIncompleteNumericDraft(raw)) return;
            const parsed = parseFieldNumber(raw);
            if (parsed != null) onChangeRef.current(parsed);
          }}
          onBlur={() => {
            field.onBlur();
            const parsed = parseFieldNumber(field.value);
            if (parsed != null) {
              commit(parsed);
              return;
            }
            if (field.value !== "" && field.value != null) {
              field.onChange(sliderValue);
            }
          }}
          onKeyDown={(event) => {
            if (event.key === "ArrowUp") {
              event.preventDefault();
              stepBy(1);
            }
            if (event.key === "ArrowDown") {
              event.preventDefault();
              stepBy(-1);
            }
          }}
          ref={setInputRef}
          sx={{
            ...numberInputSx,
            outline: error ? "1px solid" : "none",
            outlineColor: "error.main",
          }}
        />
      </Box>
      {error?.message ? (
        <Typography variant="caption" color="error">
          {error.message}
        </Typography>
      ) : null}
    </Box>
  );
};

const TextSpacingFields = ({
  control,
  errors,
  disabled = false,
  onChangeField,
}) => {
  const { t } = useTranslationApp();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <SpacingSliderField
        control={control}
        errors={errors}
        name="lineHeight"
        label={t(TEMPLATES_LINE_SPACING)}
        disabled={disabled}
        step={LINE_HEIGHT_STEP}
        sliderMin={LINE_HEIGHT_SLIDER_MIN}
        sliderMax={LINE_HEIGHT_SLIDER_MAX}
        fallback={LINE_HEIGHT_FALLBACK}
        commitMin={LINE_HEIGHT_SLIDER_MIN}
        onChangeValue={(value) => {
          const parsed =
            value === "" ? undefined : (parseFieldNumber(value) ?? undefined);
          onChangeField({
            key: "lineHeight",
            value:
              parsed == null
                ? undefined
                : Math.max(LINE_HEIGHT_SLIDER_MIN, parsed),
          });
        }}
      />
      <SpacingSliderField
        control={control}
        errors={errors}
        name="letterSpacing"
        label={t(TEMPLATES_LETTER_SPACING)}
        disabled={disabled}
        step={LETTER_SPACING_STEP}
        sliderMin={LETTER_SPACING_SLIDER_MIN}
        sliderMax={LETTER_SPACING_SLIDER_MAX}
        fallback={LETTER_SPACING_FALLBACK}
        marks={LETTER_SPACING_MARKS}
        onChangeValue={(value) =>
          onChangeField({
            key: "letterSpacing",
            value:
              value === "" ? undefined : (parseFieldNumber(value) ?? undefined),
          })
        }
      />
    </Box>
  );
};

export default TextSpacingFields;
